import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { mediaUrl } from "@/lib/api";
import { themeOf, sortEvents } from "@/lib/themes";
import { MapPin, Quote as QuoteIcon, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import MapTimeline from "@/components/timeline/MapTimeline";

const Img = ({ src, alt, style, className }) =>
  src ? <img src={mediaUrl(src)} alt={alt || ""} loading="lazy" style={style} className={className} /> : null;

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Meta = ({ ev, t }) => (
  <div className="flex flex-wrap items-center gap-2 mt-3">
    {ev.location && (
      <span className="inline-flex items-center gap-1 text-xs" style={{ color: t.muted }}>
        <MapPin size={12} /> {ev.location}
      </span>
    )}
    {(ev.tags || []).slice(0, 4).map((tag) => (
      <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full"
        style={{ background: `${t.accent}22`, color: t.accent2 }}>#{tag}</span>
    ))}
  </div>
);

const CommentPill = ({ ev, t, cc }) => cc ? (
  <button onClick={() => cc.onOpen(ev)} data-testid={`comment-pill-${ev.id}`}
    className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-transform hover:-translate-y-0.5"
    style={{ borderColor: t.surfaceBorder, color: t.muted }}>
    <MessageCircle size={12} /> {cc.counts[ev.id] ? `${cc.counts[ev.id]} comment${cc.counts[ev.id] > 1 ? "s" : ""}` : "Comment"}
  </button>
) : null;

/* ---------------------------------------------------------------- Vertical */
const Vertical = ({ events, t, cc }) => (
  <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pb-40">
    <div className="absolute left-[26px] sm:left-[30px] top-2 bottom-0 w-[2px] origin-top spine-draw" style={{ background: t.line }} />
    <div className="space-y-14 pt-6">
      {events.map((ev, i) => (
        <Reveal key={ev.id} delay={i * 0.04}>
          <div id={`ev-${ev.id}`} className="relative pl-14 sm:pl-20 scroll-mt-28">
            <span className="absolute left-[19px] sm:left-[23px] top-1.5 h-4 w-4 rounded-full ring-4"
              style={{ background: ev.color || t.dot, ringColor: t.bg, boxShadow: `0 0 0 4px ${t.bg}` }} />
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: t.accent }}>{ev.date}</div>
            <h3 className="text-2xl sm:text-3xl font-semibold leading-tight" style={{ fontFamily: t.heading, color: t.text }}>{ev.title}</h3>
            {ev.subtitle && <p className="mt-1 text-sm italic" style={{ color: t.muted }}>{ev.subtitle}</p>}
            {ev.image && (
              <div className="mt-4 overflow-hidden rounded-2xl border" style={{ borderColor: t.surfaceBorder }}>
                <Img src={ev.image} alt={ev.title} className="w-full h-56 sm:h-64 object-cover" />
              </div>
            )}
            {ev.description && <p className="mt-4 leading-relaxed" style={{ color: t.muted }}>{ev.description}</p>}
            {ev.quote && (
              <blockquote className="mt-4 pl-4 border-l-2 italic flex gap-2" style={{ borderColor: t.accent, color: t.text }}>
                <QuoteIcon size={16} style={{ color: t.accent }} /> {ev.quote}
              </blockquote>
            )}
            <Meta ev={ev} t={t} />
            <CommentPill ev={ev} t={t} cc={cc} />
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

/* ---------------------------------------------------------------- Zigzag */
const Zigzag = ({ events, t, cc }) => (
  <div className="relative max-w-5xl mx-auto px-5 pb-40">
    <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-2 bottom-0 w-[2px] origin-top spine-draw" style={{ background: t.line }} />
    <div className="space-y-10 md:space-y-16 pt-6">
      {events.map((ev, i) => {
        const left = i % 2 === 0;
        return (
          <Reveal key={ev.id} delay={i * 0.04}>
            <div id={`ev-${ev.id}`} className={`scroll-mt-28 md:grid md:grid-cols-2 md:gap-12 items-center ${left ? "" : "md:[direction:rtl]"}`}>
              <div className={`[direction:ltr] ${left ? "md:text-right" : "md:text-left"}`}>
                <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: t.accent }}>{ev.date}</div>
                <h3 className="text-2xl sm:text-3xl font-semibold leading-tight" style={{ fontFamily: t.heading, color: t.text }}>{ev.title}</h3>
                {ev.subtitle && <p className="mt-1 text-sm italic" style={{ color: t.muted }}>{ev.subtitle}</p>}
                {ev.description && <p className="mt-3 leading-relaxed" style={{ color: t.muted }}>{ev.description}</p>}
                <CommentPill ev={ev} t={t} cc={cc} />
              </div>
              <div className="[direction:ltr] mt-4 md:mt-0">
                {ev.image ? (
                  <div className="overflow-hidden rounded-2xl border" style={{ borderColor: t.surfaceBorder }}>
                    <Img src={ev.image} alt={ev.title} className="w-full h-52 object-cover" />
                  </div>
                ) : (
                  <div className="rounded-2xl border p-6" style={{ borderColor: t.surfaceBorder, background: t.surface }}>
                    <div className="text-5xl font-serif-display" style={{ color: `${ev.color || t.accent}` }}>{ev.date}</div>
                    <Meta ev={ev} t={t} />
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </div>
);

/* ---------------------------------------------------------------- Cards */
const Cards = ({ events, t, cc }) => (
  <div className="max-w-6xl mx-auto px-5 pb-40 pt-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {events.map((ev, i) => (
        <Reveal key={ev.id} delay={i * 0.04}>
          <article id={`ev-${ev.id}`} className="group rounded-2xl overflow-hidden border h-full scroll-mt-28 transition-transform duration-300 hover:-translate-y-1"
            style={{ borderColor: t.surfaceBorder, background: t.surface }}>
            {ev.image && (
              <div className="overflow-hidden h-44">
                <Img src={ev.image} alt={ev.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            )}
            <div className="p-6">
              <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: t.accent }}>{ev.date}</div>
              <h3 className="text-xl font-semibold leading-tight" style={{ fontFamily: t.heading, color: t.text }}>{ev.title}</h3>
              {ev.subtitle && <p className="mt-1 text-sm italic" style={{ color: t.muted }}>{ev.subtitle}</p>}
              {ev.description && <p className="mt-3 text-sm leading-relaxed line-clamp-4" style={{ color: t.muted }}>{ev.description}</p>}
              <Meta ev={ev} t={t} />
              <CommentPill ev={ev} t={t} cc={cc} />
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  </div>
);

/* ---------------------------------------------------------------- Horizontal */
const Horizontal = ({ events, t }) => {
  const scroller = useRef(null);
  const nudge = (dir) => scroller.current?.scrollBy({ left: dir * 460, behavior: "smooth" });
  return (
    <div className="relative pb-24 pt-6">
      <div className="flex justify-end gap-2 max-w-6xl mx-auto px-5 mb-4">
        <button onClick={() => nudge(-1)} data-testid="hz-prev" className="h-10 w-10 grid place-items-center rounded-full border transition-colors" style={{ borderColor: t.surfaceBorder, color: t.text }}><ChevronLeft size={18} /></button>
        <button onClick={() => nudge(1)} data-testid="hz-next" className="h-10 w-10 grid place-items-center rounded-full border transition-colors" style={{ borderColor: t.surfaceBorder, color: t.text }}><ChevronRight size={18} /></button>
      </div>
      <div ref={scroller} className="overflow-x-auto no-scrollbar px-5 sm:px-[10%]">
        <div className="relative inline-flex gap-8 pb-6">
          <div className="absolute left-0 right-0 top-[86px] h-[2px]" style={{ background: t.line }} />
          {events.map((ev, i) => (
            <div key={ev.id} id={`ev-${ev.id}`} className="relative w-[300px] sm:w-[360px] shrink-0 scroll-mt-28">
              <div className="text-sm font-semibold uppercase tracking-widest" style={{ color: t.accent }}>{ev.date}</div>
              <div className="h-5" />
              <span className="block h-4 w-4 rounded-full mb-8" style={{ background: ev.color || t.dot, boxShadow: `0 0 0 4px ${t.bg}` }} />
              <div className="rounded-2xl overflow-hidden border" style={{ borderColor: t.surfaceBorder, background: t.surface }}>
                {ev.image && <Img src={ev.image} alt={ev.title} className="w-full h-40 object-cover" />}
                <div className="p-5">
                  <h3 className="text-lg font-semibold leading-tight" style={{ fontFamily: t.heading, color: t.text }}>{ev.title}</h3>
                  {ev.description && <p className="mt-2 text-sm leading-relaxed line-clamp-4" style={{ color: t.muted }}>{ev.description}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Fullscreen */
const Fullscreen = ({ events, t }) => (
  <div className="snap-y snap-mandatory h-[calc(100vh-64px)] overflow-y-auto no-scrollbar">
    {events.map((ev, i) => (
      <section key={ev.id} className="snap-start h-[calc(100vh-64px)] relative grid place-items-center overflow-hidden">
        {ev.image && (
          <>
            <Img src={ev.image} alt={ev.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: t.dark ? "linear-gradient(to top, rgba(6,6,20,0.92), rgba(6,6,20,0.45))" : "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0.15))" }} />
          </>
        )}
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.5 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 max-w-3xl px-6 text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.3em] mb-4" style={{ color: ev.image ? "#fff" : t.accent }}>{ev.date}</div>
          <h2 className="text-4xl sm:text-6xl font-semibold leading-[1.05]" style={{ fontFamily: t.heading, color: ev.image ? "#fff" : t.text }}>{ev.title}</h2>
          {ev.subtitle && <p className="mt-4 text-lg italic" style={{ color: ev.image ? "rgba(255,255,255,0.85)" : t.muted }}>{ev.subtitle}</p>}
          {ev.description && <p className="mt-5 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: ev.image ? "rgba(255,255,255,0.9)" : t.muted }}>{ev.description}</p>}
          <div className="mt-8 text-xs uppercase tracking-widest" style={{ color: ev.image ? "rgba(255,255,255,0.6)" : t.muted }}>{i + 1} / {events.length}</div>
        </motion.div>
      </section>
    ))}
  </div>
);

/* ---------------------------------------------------------------- Branches */
const BRANCH_COLORS = ["#3A1F6E", "#0D9488", "#D97706", "#DB2777", "#4F46E5", "#059669"];
const Branches = ({ events, t, cc }) => {
  const groups = {};
  events.forEach((e) => { const b = (e.branch || "").trim() || "Main"; (groups[b] = groups[b] || []).push(e); });
  const names = Object.keys(groups);
  return (
    <div className="max-w-6xl mx-auto px-5 pb-40 pt-2 overflow-x-auto no-scrollbar">
      <div className="flex gap-6 min-w-min">
        {names.map((name, gi) => {
          const col = name === "Main" ? t.accent : BRANCH_COLORS[gi % BRANCH_COLORS.length];
          return (
            <div key={name} className="w-[300px] shrink-0" data-testid={`branch-${name}`}>
              <div className="mb-5 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1.5" style={{ background: `${col}22`, color: col }}>
                <span className="h-2 w-2 rounded-full" style={{ background: col }} /> {name}
              </div>
              <div className="relative pl-6">
                <div className="absolute left-[3px] top-1 bottom-1 w-[2px] rounded" style={{ background: col }} />
                <div className="space-y-5">
                  {groups[name].map((ev) => (
                    <div key={ev.id} id={`ev-${ev.id}`} className="relative scroll-mt-28">
                      <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full" style={{ background: ev.color || col, boxShadow: `0 0 0 3px ${t.bg}` }} />
                      <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: col }}>{ev.date}</div>
                      <h3 className="text-lg font-semibold leading-tight" style={{ fontFamily: t.heading, color: t.text }}>{ev.title}</h3>
                      {ev.description && <p className="mt-1.5 text-sm leading-relaxed" style={{ color: t.muted }}>{ev.description}</p>}
                      <CommentPill ev={ev} t={t} cc={cc} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function TimelineRenderer({ timeline, filter, hideHeader = false, onComment, commentCounts }) {
  const t = themeOf(timeline?.theme);
  let events = sortEvents(timeline?.events || []);
  if (filter) {
    const q = (filter.q || "").trim().toLowerCase();
    const cat = filter.category;
    events = events.filter((e) => {
      const matchQ = !q || `${e.title} ${e.subtitle} ${e.description} ${(e.tags || []).join(" ")} ${e.date}`.toLowerCase().includes(q);
      const matchCat = !cat || cat === "All" || e.category === cat;
      return matchQ && matchCat;
    });
  }
  const layout = timeline?.layout || "vertical";
  const Comp = { vertical: Vertical, zigzag: Zigzag, cards: Cards, horizontal: Horizontal, fullscreen: Fullscreen, map: MapTimeline, branches: Branches }[layout] || Vertical;
  const fullBleed = layout === "fullscreen";
  const cc = onComment ? { counts: commentCounts || {}, onOpen: onComment } : null;

  return (
    <div className="relative min-h-full grain" style={{ background: t.bgGradient }}>
      {!fullBleed && !hideHeader && (
        <header className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: t.accent }}>{timeline?.category}</div>
          <h1 className="text-4xl sm:text-6xl font-semibold leading-[1.05] tracking-tight" style={{ fontFamily: t.heading, color: t.text }}>{timeline?.title}</h1>
          {timeline?.description && <p className="mt-5 text-lg leading-relaxed" style={{ color: t.muted }}>{timeline.description}</p>}
          <div className="mt-8 mx-auto w-24 h-[3px] rounded-full" style={{ background: t.line }} />
        </header>
      )}
      {events.length === 0 ? (
        <div className="max-w-3xl mx-auto px-5 py-24 text-center" style={{ color: t.muted }}>No events match your search.</div>
      ) : (
        <Comp events={events} t={t} cc={cc} />
      )}
    </div>
  );
}
